import { useState } from "react";
import RichContent from "@/components/RichContent";
import { useQuery } from "@tanstack/react-query";
import { FileText, Check, PenLine } from "lucide-react";
import PageHero from "@/components/PageHero";
import AgreementSignModal from "@/components/AgreementSignModal";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Agreements() {
  const { user } = useAuth();
  const [signing, setSigning] = useState(null);
  const { data: agreements = [] } = useQuery({ queryKey: ["agreements"], queryFn: () => api.get("/agreements").then((r) => r.data) });
  const { data: mine = [], refetch } = useQuery({
    queryKey: ["my-signatures"], enabled: !!user,
    queryFn: () => api.get("/me/signatures").then((r) => r.data),
  });

  const signedFor = (a) => mine.find((s) => s.agreement_id === a.id && s.agreement_version === a.version);

  return (
    <div data-testid="agreements-page">
      <PageHero
        label="Agreements & Policies"
        title="Clear agreements, honored with care."
        subtitle="Review our membership, ministry, waiver, and policy agreements. Sign electronically and download a copy anytime."
        testid="agreements-hero"
      />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 space-y-4">
        {agreements.map((a) => {
          const signed = signedFor(a);
          return (
            <div key={a.id} data-testid={`agreement-${a.id}`} className="eyw-card p-6 flex items-start justify-between gap-4">
              <div className="flex gap-4">
                <span className="grid place-items-center h-11 w-11 rounded-full bg-[#144F75]/10 text-[#144F75] shrink-0"><FileText size={20} /></span>
                <div>
                  <h3 className="text-lg text-[#1A242B]">{a.title}</h3>
                  <p className="text-xs text-[#8FA89B] uppercase tracking-wide mt-0.5">{a.type} · v{a.version}{a.required_for?.length ? ` · required for ${a.required_for.join(", ")}` : ""}</p>
                  <RichContent preview text={a.content} className="text-sm text-[#4A5D6B] mt-2 line-clamp-2" />
                </div>
              </div>
              <div className="shrink-0">
                {signed ? (
                  <span className="inline-flex items-center gap-1 text-xs text-[#5C8A72] font-semibold"><Check size={14} /> Signed</span>
                ) : (
                  <button onClick={() => setSigning(a)} data-testid={`agreement-sign-${a.id}`} className="eyw-btn !py-2 !px-4 text-sm"><PenLine size={14} /> {user ? "Review & Sign" : "View"}</button>
                )}
              </div>
            </div>
          );
        })}
      </section>
      {signing && <AgreementSignModal agreement={signing} onClose={() => setSigning(null)} onSigned={refetch} />}
    </div>
  );
}
